import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const citationSchema = z.object({
  text: z.string(),
  source: z.object({
    sourceType: z.string().optional(),
    sourceDocId: z.string().optional(),
    sourceTimestamp: z.string().optional(),
    externalUrl: z.string().optional(),
  }),
  startIndex: z.number().optional(),
  endIndex: z.number().optional(),
  relevanceScore: z.number().optional(),
});

const messageSchema = z.object({
  id: z.string(),
  content: z.string(),
  citations: z.array(citationSchema).optional(),
  role: z.enum(['user', 'assistant', 'system', 'data']),
});

const threadSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  updatedAt: z.string(),
  createdAt: z.string(),
  messages: z.array(messageSchema),
});

export type MastraThread = z.infer<typeof threadSchema>;

export const mastraContract = c.router(
  {
    getChatThreads: {
      method: 'GET',
      path: '/:tenantId/threads',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          threads: z.array(threadSchema),
        }),
      },
    },
    getChatThreadMessages: {
      method: 'GET',
      path: '/:tenantId/threads/:threadId/messages',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
        includeSources: z
          .string()
          .optional()
          .transform((val) => val === 'true'),
      }),
      responses: {
        200: z.object({
          messages: z.array(messageSchema),
        }),
      },
    },
    getChatThreadTitle: {
      method: 'GET',
      path: '/:tenantId/threads/:threadId/title',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          title: threadSchema.shape.title,
        }),
      },
    },
    createChatThread: {
      method: 'POST',
      path: '/:tenantId/threads',
      pathParams: z.object({
        tenantId: z.string(),
      }),
      body: z.object({
        patientId: z.string().optional(),
      }),
      responses: {
        201: z.object({
          threadId: z.string(),
        }),
      },
    },
    updateThreadTitle: {
      method: 'PATCH',
      path: '/:tenantId/threads/:threadId/title',
      pathParams: z.object({
        tenantId: z.string(),
        threadId: z.string(),
      }),
      query: z.object({
        patientId: z.string().optional(),
      }),
      body: z.object({
        title: z.string().optional(),
        autoGenerate: z.boolean().optional(),
      }),
      responses: {
        200: z.object({
          title: z.string(),
        }),
        400: z.object({
          error: z.string(),
        }),
      },
    },
  },
  { pathPrefix: '/mastra' },
);
